import { axiosSwagger } from "@/axios";
import signalRService from "@/services/signalr.service";

function getUserDeliveryUnitId() {
  const userData =
    localStorage.getItem("USER") &&
    JSON.parse(localStorage.getItem("USER"));

  return userData?.deliveryUnit?.id;
}

/* ================================================================ */
/* State                                                             */
/* ================================================================ */
export const state = {
  // --- upload panel ---
  uploads: [],
  isOpen: false,
  isMinimized: false,

  // --- smart upload lists ---
  pending: [],
  available: [],
  accepted: [],

  loading: {
    pending: false,
    available: false,
    accepted: false,
  },

  // shared
  signalRRegistered: false,
}

/* ================================================================ */
/* Getters                                                           */
/* ================================================================ */
export const getters = {
  // upload panel
  uploading: (state) =>
    state.uploads
      .filter((u) => u.status === "uploading" || u.status === "processing")
      .slice()
      .sort((a, b) => new Date(b.dateTimeUploaded) - new Date(a.dateTimeUploaded)),

  completed: (state) =>
    state.uploads
      .filter((u) => u.status === "completed")
      .slice()
      .sort((a, b) => new Date(b.dateTimeUploaded) - new Date(a.dateTimeUploaded)),

  failed: (state) =>
    state.uploads
      .filter((u) => u.status === "error")
      .slice()
      .sort((a, b) => new Date(b.dateTimeUploaded) - new Date(a.dateTimeUploaded)),

  // smart upload lists
  pendingFiles: (state) => state.pending,
  availableFiles: (state) => state.available,
  acceptedFiles: (state) => state.accepted,
  loadingState: (state) => state.loading,
}

/* ================================================================ */
/* Mutations                                                         */
/* ================================================================ */
export const mutations = {
  // ----- upload panel -----
  OPEN_PANEL(state) {
    state.isOpen = true
  },

  CLOSE_PANEL(state) {
    state.isOpen = false
  },

  TOGGLE_MINIMIZE(state) {
    state.isMinimized = !state.isMinimized
  },

  SET_MINIMIZED(state, val) {
    state.isMinimized = val
  },

  ADD_UPLOAD(state, { fileId, fileName }) {
    if (state.uploads.find((u) => u.fileId === fileId)) return
    state.uploads.unshift({
      fileId,
      fileName,
      progress: 0,
      status: "uploading",
      errorMessage: null,
      dateTimeUploaded: new Date().toISOString(),
    })
    // Open panel immediately so user sees progress
    state.isOpen = true
    state.isMinimized = false
  },

  UPDATE_PROGRESS(state, { fileId, percent }) {
    const file = state.uploads.find((u) => u.fileId === fileId)
    if (file) {
      file.progress = percent
      if (percent >= 100) {
        file.status = "processing"
      }
    }
  },

  SET_UPLOAD_DONE(state, { fileId }) {
    const file = state.uploads.find((u) => u.fileId === fileId)
    if (file) {
      file.progress = 100
      file.status = "processing"
      file.completedAt = Date.now()
    }
  },

  SET_UPLOAD_ERROR(state, { fileId, message }) {
    const file = state.uploads.find((u) => u.fileId === fileId)
    if (file) {
      file.status = "error"
      file.errorMessage = message
    }
  },

  SET_UPLOADS(state, files) {
    state.uploads = files
  },

  // ----- smart upload lists -----
  SET_PENDING(state, payload) {
    state.pending = payload
  },

  SET_AVAILABLE(state, payload) {
    state.available = payload
  },

  SET_ACCEPTED(state, payload) {
    state.accepted = payload
  },

  SET_LOADING(state, { key, value }) {
    state.loading[key] = value
  },

  UPDATE_PENDING_PROGRESS(state, { fileId, percent }) {
    const file = state.pending.find(
      (f) => f.fileId === fileId || f.fileRealName === fileId
    )
    if (file) {
      file.progress = percent
      if (percent >= 100) {
        file.completed = true
      }
    }
  },

  SET_PENDING_ERROR(state, { fileId, message }) {
    const file = state.pending.find(
      (f) => f.fileId === fileId || f.fileRealName === fileId
    )
    if (file) {
      file.error = true
      file.errorMessage = message
    }
  },

  // ----- shared -----
  SET_SIGNALR_REGISTERED(state, val) {
    state.signalRRegistered = val
  },
}

/* ================================================================ */
/* Actions                                                           */
/* ================================================================ */
export const actions = {
  /* ============================= */
  /* SignalR Registration           */
  /* ============================= */
  registerSignalR({ commit, dispatch, state }) {
    if (state.signalRRegistered) return

    signalRService.on("ReceiveProgress", (fileId, percent) => {
      // Update upload panel progress
      commit("UPDATE_PROGRESS", { fileId, percent })

      // Update pending list progress
      commit("UPDATE_PENDING_PROGRESS", { fileId, percent })

      // When a file completes, refresh the processing lists
      if (percent >= 100) {
        setTimeout(() => {
          dispatch("fetchPending")
          dispatch("fetchAvailable")
        }, 1500)
      }
    })

    signalRService.on("ReceiveError", (fileId, message) => {
      commit("SET_UPLOAD_ERROR", { fileId, message })
      commit("SET_PENDING_ERROR", { fileId, message })
    })

    commit("SET_SIGNALR_REGISTERED", true)
  },

  unregisterSignalR({ commit }) {
    signalRService.off("ReceiveProgress")
    signalRService.off("ReceiveError")
    commit("SET_SIGNALR_REGISTERED", false)
  },

  /* ============================= */
  /* Upload File (with SignalR)     */
  /* ============================= */
  async uploadFile({ commit }, { file, fileId }) {
    // Register file in panel immediately so user sees progress
    commit("ADD_UPLOAD", { fileId, fileName: file.name })

    const deliveryUnitId = getUserDeliveryUnitId()

    const formData = new FormData()
    formData.append("file", file)

    const url = `/api/AiFileManager/Upload/${deliveryUnitId}/${fileId}/${file.size}`

    try {
      await axiosSwagger.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      // HTTP request completed — mark done
      commit("SET_UPLOAD_DONE", { fileId })
    } catch (error) { 
      console.error(`Error uploading ${file.name}:`, error)
      commit("SET_UPLOAD_ERROR", {
        fileId,
        message:
          error?.response?.data?.message || error.message || "Upload failed",
      })
    }
  },

  /* ============================= */
  /* Fetch Uploads (initial load)   */
  /* ============================= */
  async fetchUploads({ commit, state }) {
    const deliveryUnitId = getUserDeliveryUnitId()

    try {
      const [pendingRes, availableRes] = await Promise.all([
        axiosSwagger.get(`/api/AiFileManager/Pending/${deliveryUnitId}`),
        axiosSwagger.get(`/api/AiFileManager/Available/${deliveryUnitId}`),
      ])

      const pending = pendingRes.data.map((f) => ({
        ...f,
        fileId: f.fileId || `server-${f.id}`,
        fileName: f.fileName,
        progress: 70,
        status: "processing",
      }))

      const available = availableRes.data.map((f) => ({
        ...f,
        fileId: f.fileId || `server-${f.id}`,
        fileName: f.fileName,
        progress: 100,
        status: "completed",
      }))

      // Preserve active uploads being tracked via SignalR
      const activeUploads = state.uploads.filter(
        (u) => u.status === "uploading" || u.status === "processing"
      )
      const activeFileIds = new Set(activeUploads.map((u) => u.fileId))
      const serverFiles = [...pending, ...available].filter(
        (f) => !activeFileIds.has(f.fileId)
      )

      commit("SET_UPLOADS", [...activeUploads, ...serverFiles])

      // Auto-show minimized FAB if there are completed files on initial load
      const hasCompleted = available.length > 0
      if (hasCompleted) {
        commit("OPEN_PANEL")
        commit("SET_MINIMIZED", true)
      }
    } catch (error) {
      console.error("AI upload fetch error:", error)
    }
  },

  async reFetchUploads({ commit, state }) {
    const deliveryUnitId = getUserDeliveryUnitId()

    try {
      const [pendingRes, availableRes] = await Promise.all([
        axiosSwagger.get(`/api/AiFileManager/Pending/${deliveryUnitId}`),
        axiosSwagger.get(`/api/AiFileManager/Available/${deliveryUnitId}`),
      ])

      const pending = (pendingRes.data || []).map((f) => ({
        ...f,
        fileId: f.fileId || `server-${f.id}`,
        fileName: f.fileName,
        progress: 70,
        status: "processing",
      }))

      const available = (availableRes.data || []).map((f) => ({
        ...f,
        fileId: f.fileId || `server-${f.id}`,
        fileName: f.fileName,
        progress: 100,
        status: "completed",
      }))

      // Keep only uploads actively being tracked via SignalR (real-time uploads)
      const activeUploads = state.uploads.filter(
        (u) => u.status === "uploading"
      )
      const activeFileIds = new Set(activeUploads.map((u) => u.fileId))
      const serverFiles = [...pending, ...available].filter(
        (f) => !activeFileIds.has(f.fileId)
      )

      commit("SET_UPLOADS", [...activeUploads, ...serverFiles])

      // Always open and expand the panel — this is called because a notification arrived
      commit("OPEN_PANEL")
      commit("SET_MINIMIZED", false)
    } catch (error) {
      console.error("AI upload re-fetch error:", error)
    }
  },

  /* ============================= */
  /* Fetch Pending                  */
  /* ============================= */
  async fetchPending({ commit }) {
    commit("SET_LOADING", { key: "pending", value: true })

    const deliveryUnitId = getUserDeliveryUnitId()

    try {
      const res = await axiosSwagger.get(
        `/api/AiFileManager/Pending/${deliveryUnitId}`
      )

      const files = (res.data || []).map((f) => ({
        ...f,
        progress: f.completed ? 100 : f.progress || 0,
        error: false,
        errorMessage: null,
      }))

      commit("SET_PENDING", files)
    } catch (err) {
      console.error(err)
    } finally {
      commit("SET_LOADING", { key: "pending", value: false })
    }
  },

  /* ============================= */
  /* Fetch Available                */
  /* ============================= */
  async fetchAvailable({ commit }) {
    commit("SET_LOADING", { key: "available", value: true })

    const deliveryUnitId = getUserDeliveryUnitId()

    try {
      const res = await axiosSwagger.get(
        `/api/AiFileManager/Available/${deliveryUnitId}`
      )
      commit("SET_AVAILABLE", res.data || [])
    } catch (err) {
      console.error(err)
    } finally {
      commit("SET_LOADING", { key: "available", value: false })
    }
  },

  /* ============================= */
  /* Fetch Accepted                 */
  /* ============================= */
  async fetchAccepted({ commit }) {
    commit("SET_LOADING", { key: "accepted", value: true })

    const deliveryUnitId = getUserDeliveryUnitId()

    try {
      const res = await axiosSwagger.get(
        `/api/AiFileManager/Accepted/${deliveryUnitId}`
      )
      commit("SET_ACCEPTED", res.data || [])
    } catch (err) {
      console.error(err)
    } finally {
      commit("SET_LOADING", { key: "accepted", value: false })
    }
  },

  /* ============================= */
  /* Accept / Update File           */
  /* ============================= */
  async acceptFile(_, { id, fileFolderId }) {
    await axiosSwagger.post(`/api/AiFileManager/Accept/${id}/${fileFolderId}`)
  },

  async updateFile(_, { id, payload }) {
    await axiosSwagger.post(`/api/AiFileManager/UpdateDetails/${id}`, payload)
  },

  async testIndexing(_, { id }) {
    await axiosSwagger.get(`/api/AiFileManager/TestIndex/${id}`)
  },

  openSmartUploadPanel({ commit }) {
    commit("OPEN_PANEL")
    commit("SET_MINIMIZED", false)
  }
}
